import {
  readMaturaCourseCustomPlan,
  readMaturaCourseTarget,
  saveMaturaCourseCustomPlan,
  saveMaturaCourseTarget,
} from "./maturaCourseProgress";

const STORAGE_KEY = "matematron.maturaDemo.quizSnapshots.v1";
const EXAM_HISTORY_KEY = "matematron.maturaDemo.examAttempts.v1";
const SERVER_SESSION_KEY = "matematron.maturaDemo.serverSession.v1";
const VISITOR_KEY = "mt_visitor_id";

export function getOrCreateDemoMaturaSessionId() {
  if (typeof window === "undefined") return "";
  try {
    let value = window.localStorage.getItem(SERVER_SESSION_KEY);
    if (!value) {
      value = window.crypto?.randomUUID?.().replace(/-/g, "")
        || `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(SERVER_SESSION_KEY, value);
    }
    return value;
  } catch {
    return "";
  }
}

async function uploadTemporaryProgress(progressPayload, request = null) {
  const requestFn = request || (typeof fetch === "function" ? fetch : null);
  if (typeof window === "undefined" || typeof requestFn !== "function" || !progressPayload) return false;
  const sessionId = getOrCreateDemoMaturaSessionId();
  if (!sessionId) return false;
  try {
    const response = await requestFn("/api/demo-matura/state", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        visitorId: window.localStorage.getItem(VISITOR_KEY) || "",
        payload: progressPayload,
      }),
    });
    return response.ok;
  } catch {
    // Lokalny snapshot pozostaje źródłem awaryjnym przy braku sieci.
    return false;
  }
}

export async function claimDemoMaturaSession(authorizedFetch, userEmail = "") {
  if (typeof window === "undefined" || typeof authorizedFetch !== "function") return false;
  const sessionId = window.localStorage.getItem(SERVER_SESSION_KEY);
  if (!sessionId) return false;
  try {
    const localProgressPayload = readDemoMaturaSnapshots()
      .find((snapshot) => snapshot?.temporaryProgressPayload)
      ?.temporaryProgressPayload;
    const response = await authorizedFetch("/api/demo-matura/claim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId,
        // Awaryjne źródło tylko wtedy, gdy wcześniejszy zapis do tymczasowej
        // sesji serwerowej się nie udał. Backend ignoruje je, gdy ma już dane.
        fallbackProgressPayload: localProgressPayload || null,
        settings: {
          target: readMaturaCourseTarget("__demo__"),
          customPlan: readMaturaCourseCustomPlan("__demo__"),
        },
      }),
    });
    if (!response.ok) return false;
    const data = await response.json();
    window.localStorage.removeItem(SERVER_SESSION_KEY);
    if (data?.claimed) {
      if (userEmail) {
        saveMaturaCourseTarget(readMaturaCourseTarget("__demo__"), userEmail);
        saveMaturaCourseCustomPlan(readMaturaCourseCustomPlan("__demo__"), userEmail);
      }
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(EXAM_HISTORY_KEY);
    }
    return Boolean(data?.claimed);
  } catch {
    return false;
  }
}

export function readDemoMaturaSnapshots() {
  if (typeof window === "undefined") return [];
  try {
    const rows = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

export function readDemoMaturaExamHistory() {
  if (typeof window === "undefined") return [];
  try {
    const rows = JSON.parse(window.localStorage.getItem(EXAM_HISTORY_KEY) || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

export function saveDemoMaturaExamAttempt({ points, maxPoints, mode, upload = true }) {
  if (typeof window === "undefined") return null;
  try {
    const safePoints = Math.max(0, Number(points) || 0);
    const safeMax = Math.max(0, Number(maxPoints) || 0);
    const row = {
      id: `demo-exam-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      snapshotId: null,
      points: safePoints,
      maxPoints: safeMax,
      percent: safeMax > 0 ? Math.round((safePoints / safeMax) * 100) : 0,
      mode: mode === "exam" ? "exam" : "training",
      finishedAt: new Date().toISOString(),
      includeInStats: true,
      localOnly: true,
    };
    const rows = readDemoMaturaExamHistory();
    window.localStorage.setItem(EXAM_HISTORY_KEY, JSON.stringify([...rows, row].slice(-20)));
    if (upload) void uploadTemporaryProgress({
      taskId: "matura_exam_A",
      topicId: "matura_exam_A",
      score: safeMax > 0 ? safePoints / safeMax : 0,
      maxPoints: safeMax,
      snapshot: {
        title: "Arkusz maturalny — podejście z demo",
        maxPoints: safeMax,
        version: "matura-demo:v1",
        questions: [],
      },
    });
    return row;
  } catch {
    return null;
  }
}

export function readDemoMaturaSnapshot(id) {
  return readDemoMaturaSnapshots().find((snapshot) => snapshot?.id === id) || null;
}

function valueToText(value) {
  if (value == null || value === "") return "";
  if (["string", "number", "boolean"].includes(typeof value)) return String(value);
  if (Array.isArray(value)) return value.map(valueToText).filter(Boolean).join(", ");
  if (typeof value === "object") {
    for (const key of ["raw", "latex", "text", "expr", "value"]) {
      if (value[key] != null && typeof value[key] !== "object") return String(value[key]);
    }
  }
  return "";
}

function toSnapshotDocument(snapshot, id, createdAt) {
  const tasks = Array.isArray(snapshot.tasks) ? snapshot.tasks : [];
  const answers = Array.isArray(snapshot.answers) ? snapshot.answers : [];
  const feedback = Array.isArray(snapshot.feedback) ? snapshot.feedback : [];
  const whiteboards = Array.isArray(snapshot.taskWhiteboards) ? snapshot.taskWhiteboards : [];
  const maxPoints = Number(snapshot.maxScore) || tasks.length;
  const pointsEarned = Number(snapshot.score) || 0;
  return {
    id,
    sectionTitle: snapshot.sectionTitle,
    title: snapshot.snapshotTitle || snapshot.sectionTitle || "Lokalne podejście",
    createdAt,
    createdBy: "Tryb demonstracyjny",
    pointsEarned,
    maxPoints,
    score: maxPoints > 0 ? pointsEarned / maxPoints : 0,
    percent: maxPoints > 0 ? Math.round((pointsEarned / maxPoints) * 100) : 0,
    localOnly: true,
    temporaryProgressPayload: snapshot.progressPayload || null,
    questions: tasks.map((task, index) => {
      const answer = answers[index];
      const result = feedback[index] || {};
      const correctAnswer = task?.answer?.answer ?? task?.answer ?? null;
      return {
        id: task?.id || `q${index + 1}`,
        type: task?.type || task?.meta?.type || task?.questionType || null,
        task,
        userAnswer: answer,
        correctAnswer,
        isCorrect: Boolean(result.isCorrect),
        score: Number.isFinite(result.score) ? result.score : null,
        maxScore: Number.isFinite(result.maxScore) ? result.maxScore : null,
        parts: result.parts || null,
        whiteboard: whiteboards[index] || null,
        display: {
          statement: task?.prompt ?? task?.title ?? task?.statement ?? task?.text ?? null,
          user: valueToText(answer),
          correct: valueToText(correctAnswer),
          hasAnswer: valueToText(answer) !== "",
        },
      };
    }),
  };
}

export function buildDemoMaturaProgress() {
  return readDemoMaturaSnapshots().reduce((progress, snapshot) => {
    const taskId = String(snapshot?.sectionTitle || "");
    if (!taskId) return progress;
    const score = Number(snapshot?.percent) || 0;
    progress[taskId] = {
      score,
      bestScore: score,
      attempts: 1,
      timestamp: snapshot.createdAt || null,
      bestTimestamp: snapshot.createdAt || null,
      localOnly: true,
      lastSnapshotId: snapshot.id,
      bestSnapshotId: snapshot.id,
    };
    return progress;
  }, {});
}

export function saveDemoMaturaSnapshot(snapshot) {
  if (typeof window === "undefined" || !snapshot) return null;
  try {
    const id = `demo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const entry = toSnapshotDocument(snapshot, id, new Date().toISOString());
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([entry]));
    void uploadTemporaryProgress(snapshot.progressPayload);
    return entry;
  } catch {
    return null;
  }
}
